import React from 'react';
import { Menu, MenuItem, makeStyles } from '@material-ui/core';

export interface FilterMenuOption {
  type: string;
  label: string;
  description: string;
  icon?: React.ReactElement;
  disabled?: boolean;
  dimension?: string;
}

interface AddFilterMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onCancelClose?: () => void;
  onSelect: (type: string) => void;
  options: FilterMenuOption[];
}

const useStyles = makeStyles({
  menu: {
    '& .MuiList-padding': {
      padding: 0,
    },
    '@media (hover: hover)': {
      pointerEvents: 'none',
    },
  },
  menuPaper: {
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    minWidth: 260,
    marginTop: 4,
    pointerEvents: 'auto',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
    '&:active': {
      backgroundColor: '#E5E5E5',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
  },
  iconWrap: {
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    fontSize: 20,
    '& svg': {
      fontSize: 20,
    },
  },
  textWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    lineHeight: 1.3,
  },
  description: {
    fontSize: 12,
    color: '#999',
    lineHeight: 1.3,
  },
});

const AddFilterMenu = ({ anchorEl, onClose, onCancelClose, onSelect, options }: AddFilterMenuProps) => {
  const classes = useStyles();

  return (
    <Menu
      className={classes.menu}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      getContentAnchorEl={null}
      autoFocus={false}
      variant="menu"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        className: classes.menuPaper,
        onMouseEnter: () => onCancelClose?.(),
        onMouseLeave: onClose,
      }}
      BackdropProps={{
        invisible: true,
        style: window.matchMedia('(hover: hover)').matches ? { pointerEvents: 'none' } : undefined,
      }}
    >
      {options.map(option => (
        <MenuItem
          key={option.type}
          onClick={() => { onSelect(option.type); onClose(); }}
          disabled={option.disabled}
          disableRipple
          className={classes.menuItem}
        >
          {option.icon && <div className={classes.iconWrap}>{option.icon}</div>}
          <div className={classes.textWrap}>
            <span className={classes.label}>{option.label}</span>
            <span className={classes.description}>{option.description}</span>
          </div>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default AddFilterMenu;
